// Evaluates what a user types into an amount field, so "100+100" or "1,200*3/4" become numbers.
// Supports + - * / and parentheses; anything else makes the expression invalid (returns null).

const tokenize = (input) => {
    const tokens = [];
    let i = 0;
    while (i < input.length) {
        const ch = input[i];
        if ('+-*/()'.includes(ch)) {
            tokens.push(ch);
            i += 1;
        } else if (/[\d.]/.test(ch)) {
            let j = i;
            while (j < input.length && /[\d.]/.test(input[j])) j += 1;
            const number = Number(input.slice(i, j));
            if (!Number.isFinite(number)) return null;
            tokens.push(number);
            i = j;
        } else {
            return null;
        }
    }
    return tokens;
};

export function evaluateAmountExpression(raw) {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

    const input = String(raw).replace(/[,\s₪]/g, '').replace(/×/g, '*').replace(/÷/g, '/');
    if (!input) return null;
    const tokens = tokenize(input);
    if (!tokens || tokens.length === 0) return null;

    let pos = 0;
    const peek = () => tokens[pos];
    const next = () => tokens[pos++];

    // expression := term (('+' | '-') term)*
    // term       := factor (('*' | '/') factor)*
    // factor     := ('+' | '-') factor | number | '(' expression ')'
    const factor = () => {
        const token = next();
        if (token === '-') return -factor();
        if (token === '+') return factor();
        if (typeof token === 'number') return token;
        if (token === '(') {
            const value = expression();
            if (next() !== ')') throw new Error('unbalanced');
            return value;
        }
        throw new Error('unexpected');
    };
    const term = () => {
        let value = factor();
        while (peek() === '*' || peek() === '/') {
            const op = next();
            const right = factor();
            value = op === '*' ? value * right : value / right;
        }
        return value;
    };
    const expression = () => {
        let value = term();
        while (peek() === '+' || peek() === '-') {
            const op = next();
            const right = term();
            value = op === '+' ? value + right : value - right;
        }
        return value;
    };

    try {
        const value = expression();
        if (pos !== tokens.length || !Number.isFinite(value)) return null;
        return value;
    } catch {
        return null;
    }
}

/** True when the text is just a number, not an expression still being typed. */
export const isPlainNumber = (raw) => /^-?(\d+\.?\d*|\.\d+)$/.test(String(raw ?? '').replace(/,/g, '').trim());
